import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Pencil, AlertTriangle, X, Loader2 } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TemplatePreviewCard from "./templates/TemplatePreviewCard";
import CreateEditTemplateDialog from "./templates/CreateEditTemplateDialog";

function DeleteConfirmDialog({ template, open, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!open || !template) return null;
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/platform/templates/${template.template_key}`);
      toast.success(`Template "${template.template_name}" dihapus.`);
      onDeleted?.();
      onClose?.();
    } catch (e) { notifyError(e, "Gagal menghapus template"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="template-delete-dialog"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="w-full max-w-md bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-500/15 grid place-items-center">
            <AlertTriangle size={16} className="text-rose-400" />
          </div>
          <div className="text-white font-display font-black text-base">Hapus Template?</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="p-6 text-sm text-white/80">
          <p>Hapus <strong className="text-white">{template.template_name}</strong> (<span className="font-mono text-white/60">{template.template_key}</span>)?</p>
          <p className="text-xs text-white/50 mt-2">Template yang masih dipakai tenant tidak bisa dihapus (server akan menolak).</p>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={doDelete} disabled={busy} className="bg-rose-500 text-white hover:bg-rose-600" data-testid="template-delete-confirm">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menghapus…</> : "Hapus"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PlatformTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: "create" | "edit", template?: ... }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/templates");
      setItems(r.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = items.reduce((acc, t) => {
    const c = t.template_category || "other";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});

  return (
    <PlatformLayout title="Templates">
      <div data-testid="platform-templates-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="text-xs text-white/50">Total: {items.length} templates</div>
          <Button onClick={() => setDialog({ mode: "create" })} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-add-template">
            <Plus size={14} className="mr-1" /> Tambah Template
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, tpls]) => (
              <div key={cat}>
                <div className="text-[10px] font-black uppercase tracking-wider text-white/40 mb-2">{cat} · {tpls.length}</div>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {tpls.map((t) => (
                    <div key={t.template_key} className="group relative" data-testid={`template-list-${t.template_key}`}>
                      <TemplatePreviewCard template={t} />
                      <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setDialog({ mode: "edit", template: t })}
                          className="w-7 h-7 rounded-lg bg-black/60 backdrop-blur text-white/80 hover:text-white grid place-items-center"
                          data-testid={`btn-edit-template-${t.template_key}`}>
                          <Pencil size={11} />
                        </button>
                        <button onClick={() => setDeleteTarget(t)}
                          className="w-7 h-7 rounded-lg bg-black/60 backdrop-blur text-rose-400 hover:text-rose-300 grid place-items-center"
                          data-testid={`btn-delete-template-${t.template_key}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <CreateEditTemplateDialog
          mode={dialog?.mode || "create"}
          template={dialog?.template}
          open={!!dialog}
          onClose={() => setDialog(null)}
          onSaved={load}
        />
        <DeleteConfirmDialog
          template={deleteTarget}
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={load}
        />
      </div>
    </PlatformLayout>
  );
}
